import React, { useState, useEffect } from 'react';
import { useLanguage } from './context/LanguageContext';
import { useStore } from './context/StoreContext';
import { useCart } from './context/CartContext';

// Layout Components
import TopBanner from './components/layout/TopBanner';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Home Components
import HeroSection from './components/home/HeroSection';
import TrustBadges from './components/home/TrustBadges';
import CategoryGrid from './components/home/CategoryGrid';
import FeaturedProducts from './components/home/FeaturedProducts';
import HakimProfileBanner from './components/home/HakimProfileBanner';
import TibbiDiagnosisGuide from './components/home/TibbiDiagnosisGuide';
import FaqSection from './components/home/FaqSection';
import ContactPage from './components/home/ContactPage';
import AboutUsPage from './components/home/AboutUsPage';

// Blog Components
import BlogSection from './components/blog/BlogSection';
import BlogListPage from './components/blog/BlogListPage';
import ArticleDetailPage from './components/blog/ArticleDetailPage';

// Shop Components
import ProductCard from './components/shop/ProductCard';
import ProductDetailModal from './components/shop/ProductDetailModal';
import ProductDetailPage from './components/shop/ProductDetailPage';
import TibbiFilter from './components/shop/TibbiFilter';

// Cart & Checkout Components
import CartDrawer from './components/cart/CartDrawer';
import CheckoutModal from './components/checkout/CheckoutModal';
import SocialProofToast from './components/layout/SocialProofToast';

// Admin Components
import AdminDashboard from './components/admin/AdminDashboard';
import AdminLoginModal from './components/admin/AdminLoginModal';
import { scoreProductMatch } from './utils/searchHelper';
import { updateDocumentSeo } from './utils/seoHelpers';

import { 
  Sparkles, 
  MessageCircle, 
  Search, 
  Filter, 
  Package, 
  Award, 
  HeartPulse, 
  PhoneCall,
  MapPin,
  SlidersHorizontal,
  X,
  Check
} from 'lucide-react';

function App() {
  const { lang, t } = useLanguage();
  const { 
    settings, 
    products, 
    isAdminAuthenticated, 
    categories, 
    blogs, 
    mizajList, 
    ailments, 
    getBlogBySlug, 
    getBlogById 
  } = useStore();
  const { 
    setSelectedProductModal, 
    selectedProductModal, 
    selectedProduct, 
    setSelectedProduct, 
    viewProductDetail 
  } = useCart();

  // Navigation state: 'home', 'shop', 'categories', 'blogs', 'article-detail', 'tibbi-guide', 'about-hakim', 'contact', 'product-detail', 'admin'
  const [activeTab, setActiveTab] = useState('home');
  const [adminLoginOpen, setAdminLoginOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Shop Filter State (MUST be declared before any useEffect hooks)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMizaj, setSelectedMizaj] = useState('all');
  const [selectedAilments, setSelectedAilments] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const activeFiltersCount = (selectedCategory !== 'all' ? 1 : 0) + 
                             (selectedMizaj !== 'all' ? 1 : 0) + 
                             selectedAilments.length + 
                             (inStockOnly ? 1 : 0) + 
                             (searchQuery.trim() !== '' ? 1 : 0);

  // Handle URL query parameter for direct article & product links
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const articleParam = urlParams.get('article');
      if (articleParam && blogs?.length) {
        const found = getBlogBySlug(articleParam) || getBlogById(articleParam);
        if (found) {
          setSelectedArticle(found);
          setSelectedProduct(null);
          setActiveTab('article-detail');
        }
      }
      const productParam = urlParams.get('product');
      if (productParam && products?.length) {
        const foundProd = products.find(p => 
          p.id === productParam || 
          p.nameEn?.toLowerCase().replace(/[^a-z0-9]+/g, '-') === productParam
        );
        if (foundProd) {
          setSelectedProduct(foundProd);
          setSelectedArticle(null);
          setActiveTab('product-detail');
        }
      }
    }
  }, [blogs, products]);

  // Dynamic SEO Title, Description & OpenGraph Tags
  useEffect(() => {
    try {
      if (activeTab === 'product-detail' && selectedProduct) {
        const prodName = lang === 'ur' ? (selectedProduct.nameUrdu || '') : (selectedProduct.nameEn || selectedProduct.nameUrdu || '');
        const enPart = selectedProduct.nameEn ? ` (${selectedProduct.nameEn})` : '';
        const title = `${prodName}${enPart} | ہمالین پنسار سٹور`;
        const benefits = typeof selectedProduct.benefitsUrdu === 'string' ? selectedProduct.benefitsUrdu : '';
        const description = benefits
          ? `${benefits.slice(0, 140)}... خالص قدرتی جڑی بوٹی۔ کیش آن ڈلیوری پاکستان۔`
          : `خالص قدرتی ${selectedProduct.nameUrdu || ''} آن لائن خریدیں۔ ہمالین پنسار سٹور پر مستند ہربل علاج اور کیش آن ڈلیوری۔`;
        updateDocumentSeo({
          title,
          description,
          image: selectedProduct.image,
          type: 'product'
        });
      } else if (activeTab === 'article-detail' && selectedArticle) {
        const artTitle = lang === 'ur' ? (selectedArticle.titleUrdu || '') : (selectedArticle.titleEn || selectedArticle.titleUrdu || '');
        const title = `${artTitle} | طبِ پاکستانی بلاگ - ہمالین پنسار سٹور`;
        const description = selectedArticle.excerptUrdu || selectedArticle.summaryUrdu || 'طبِ پاکستانی اور قانون مفرد اعضاء پر مستند تحقیقی مضامین اور رہنمائی۔';
        updateDocumentSeo({
          title,
          description,
          image: selectedArticle.image,
          type: 'article'
        });
      } else if (activeTab === 'shop') {
        const catObj = Array.isArray(categories) ? categories.find(c => c.id === selectedCategory) : null;
        const catName = catObj ? (lang === 'ur' ? catObj.nameUrdu : catObj.nameEn) : 'تمام مصنوعات';
        const title = `${catName} - آن لائن خریداری | ہمالین پنسار سٹور`;
        const description = `خالص جڑی بوٹیاں، معجون، روغن اور دیسی ادویات کی مکمل کیٹلاگ۔ بااعتماد آن لائن ہربل سٹور، کیش آن ڈلیوری پاکستان۔`;
        updateDocumentSeo({ title, description, type: 'website' });
      } else if (activeTab === 'categories') {
        updateDocumentSeo({
          title: 'جڑی بوٹیوں و ادویات کی تمام کیٹیگریز | ہمالین پنسار سٹور',
          description: 'مفرد جڑی بوٹیاں، معجون جات، جوارشات، خمیرہ جات، اور نباتاتی روغن کی مکمل درجہ بندی اور فہرست۔',
          type: 'website'
        });
      } else if (activeTab === 'blogs') {
        updateDocumentSeo({
          title: 'طبی مضامین و رہنمائی (ہربل بلاگ) | ہمالین پنسار سٹور',
          description: 'طبِ پاکستانی (قانون مفرد اعضاء از صابر ملتانیؒ)، نبض و قارورہ، اور دیسی جڑی بوٹیوں کے خواص پر مستند طبی مضامین۔',
          type: 'website'
        });
      } else if (activeTab === 'tibbi-guide') {
        updateDocumentSeo({
          title: 'طبِ پاکستانی تشخیص و رہنمائے علاج | دواخانہ طب پاکستانی',
          description: 'قانون مفرد اعضاء کے تحت اعصابی، عضلاتی اور غدی امراض کی علامات اور تفصیلی طبی تشخیص گائیڈ۔',
          type: 'website'
        });
      } else if (activeTab === 'about-hakim') {
        updateDocumentSeo({
          title: 'تعارف حکیم مولانا سید عبدالوہاب شاہ شیرازی | ہمالین پنسار سٹور',
          description: 'فاضل طب والجراحت (FTJ)، ماہر قانون مفرد اعضاء (طبِ پاکستانی)۔ مستند روایتی طب و نباتاتی علاج کی تفصیلات۔',
          type: 'profile'
        });
      } else if (activeTab === 'contact') {
        updateDocumentSeo({
          title: 'رابطہ کریں و واٹس ایپ مشاورت | ہمالین پنسار سٹور',
          description: 'حکیم صاحب سے طبی مشورے، واٹس ایپ آرڈر اور دواخانے کے پتے کی تفصیلات کے لیے رابطہ کریں۔',
          type: 'website'
        });
      } else {
        updateDocumentSeo({
          title: 'ہمالین پنسار سٹور | دواخانہ طب پاکستانی - حکیم مولانا سید عبدالوہاب شاہ شیرازی',
          description: 'خالص قدرتی جڑی بوٹیاں، روغن و عرقیات، معجون، جوارش، خمیرہ جات، اور کشتہ جات کی مستند آن لائن خریداری۔ کیش آن ڈلیوری اور واٹس ایپ آرڈر برائے تمام پاکستان۔',
          type: 'website'
        });
      }
    } catch (e) {
      console.warn('SEO effect error:', e);
    }
  }, [activeTab, selectedProduct, selectedArticle, selectedCategory, lang, categories]);

  const toggleAilment = (ailmentId) => {
    setSelectedAilments(prev =>
      prev.includes(ailmentId)
        ? prev.filter(id => id !== ailmentId)
        : [...prev, ailmentId]
    );
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedMizaj('all');
    setSelectedAilments([]);
    setInStockOnly(false);
  };

  const handleSelectCategoryFromHome = (categoryId) => {
    setSelectedProduct(null);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('product');
      window.history.pushState({}, '', url);
    }
    setSelectedCategory(categoryId);
    setActiveTab('shop');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filtered Products Logic
  const filteredProducts = products.filter(product => {
    // Intelligent Search query matching (Urdu/English normalizer, synonyms & scoring)
    if (searchQuery.trim() !== '') {
      const matchScore = scoreProductMatch(product, searchQuery);
      if (matchScore <= 0) return false;
    }

    // Category
    if (selectedCategory !== 'all' && product.category !== selectedCategory) {
      return false;
    }

    // Mizaj (قانون مفرد اعضاء و تحاریک)
    if (selectedMizaj !== 'all') {
      const legacyMap = {
        'garam-khushk': ['ghaddi-azlati', 'azlati-ghaddi'],
        'garam-tar': ['ghaddi-asabi'],
        'sard-tar': ['asabi-ghaddi', 'asabi-azlati'],
        'sard-khushk': ['azlati-asabi'],
        'motadil': ['motadil']
      };
      const matchesDirect = product.mizaj === selectedMizaj;
      const matchesLegacy = legacyMap[product.mizaj]?.includes(selectedMizaj);
      if (!matchesDirect && !matchesLegacy) {
        return false;
      }
    }

    // Ailments
    if (selectedAilments.length > 0) {
      const hasAilment = selectedAilments.some(ail => product.ailments?.includes(ail));
      if (!hasAilment) return false;
    }

    // In-stock only
    if (inStockOnly && !product.inStock) {
      return false;
    }

    return true;
  });

  // If viewing Admin mode
  if (activeTab === 'admin' && isAdminAuthenticated) {
    return <AdminDashboard onBackToStore={() => setActiveTab('home')} />;
  }

  return (
    <div className="min-h-screen bg-herbal-cream flex flex-col selection:bg-pansar-200">
      
      {/* Top Banner Announcement & Dynamic Smart Ticker */}
      <TopBanner
        onSelectProduct={(prod) => {
          if (viewProductDetail) {
            viewProductDetail(prod);
          } else {
            setSelectedProduct(prod);
          }
          setActiveTab('product-detail');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onSelectArticle={(art) => {
          setSelectedProduct(null);
          setSelectedArticle(art);
          setActiveTab('article-detail');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* Main Navbar */}
      <Navbar
        activeTab={activeTab}
        setActiveTab={(tabId) => {
          if (tabId !== 'product-detail') {
            setSelectedProduct(null);
          }
          if (tabId !== 'article-detail') {
            setSelectedArticle(null);
          }
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            if (tabId !== 'product-detail') url.searchParams.delete('product');
            if (tabId !== 'article-detail') url.searchParams.delete('article');
            window.history.pushState({}, '', url);
          }
          setActiveTab(tabId);
        }}
        onSelectProduct={(prod) => {
          setSelectedArticle(null);
          if (viewProductDetail) {
            viewProductDetail(prod);
          } else {
            setSelectedProduct(prod);
          }
          setActiveTab('product-detail');
          if (typeof window !== 'undefined' && prod?.id) {
            const url = new URL(window.location.href);
            url.searchParams.set('product', prod.id);
            url.searchParams.delete('article');
            window.history.pushState({}, '', url);
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onOpenAdminLogin={() => {
          if (isAdminAuthenticated) {
            setActiveTab('admin');
          } else {
            setAdminLoginOpen(true);
          }
        }}
      />

      {/* VIEW: DEDICATED ARTICLE DETAIL FULL PAGE */}
      {((activeTab === 'article-detail' || Boolean(selectedArticle)) && activeTab !== 'admin') ? (
        <main className="flex-1">
          <ArticleDetailPage
            article={selectedArticle}
            onBackToHome={() => {
              setSelectedArticle(null);
              setActiveTab('home');
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.delete('article');
                window.history.pushState({}, '', url);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onBackToBlogs={() => {
              setSelectedArticle(null);
              setActiveTab('blogs');
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.delete('article');
                window.history.pushState({}, '', url);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onSelectArticle={(art) => {
              setSelectedArticle(art);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onSelectProduct={(prod) => {
              setSelectedArticle(null);
              if (viewProductDetail) {
                viewProductDetail(prod);
              } else {
                setSelectedProduct(prod);
              }
              setActiveTab('product-detail');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      ) : ((activeTab === 'product-detail' || Boolean(selectedProduct)) && activeTab !== 'admin') ? (
        <main className="flex-1">
          <ProductDetailPage
            product={selectedProduct}
            onBackToHome={() => {
              setSelectedProduct(null);
              setActiveTab('home');
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.delete('product');
                window.history.pushState({}, '', url);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onBackToShop={() => {
              setSelectedProduct(null);
              setActiveTab('shop');
              if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.delete('product');
                window.history.pushState({}, '', url);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onSelectProduct={(prod) => {
              if (viewProductDetail) {
                viewProductDetail(prod);
              } else {
                setSelectedProduct(prod);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onSelectArticle={(art) => {
              setSelectedProduct(null);
              setSelectedArticle(art);
              setActiveTab('article-detail');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      ) : (
        <>
          {/* VIEW 1: HOME PAGE */}
          {activeTab === 'home' && (
        <main className="flex-1">
          <HeroSection
            onShopClick={() => {
              setActiveTab('shop');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onTibbiClick={() => {
              setActiveTab('tibbi-guide');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onConsultClick={() => {
              setActiveTab('contact');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />

          <TrustBadges />

          <CategoryGrid onSelectCategory={handleSelectCategoryFromHome} />

          <FeaturedProducts
            onShopClick={() => {
              setActiveTab('shop');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onSelectCategory={handleSelectCategoryFromHome}
          />

          <TibbiDiagnosisGuide products={products} />

          <BlogSection
            onSelectArticle={(article) => {
              setSelectedProduct(null);
              setSelectedArticle(article);
              setActiveTab('article-detail');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onViewAllArticles={() => {
              setSelectedProduct(null);
              setSelectedArticle(null);
              setActiveTab('blogs');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />

          <FaqSection
            onContactClick={() => {
              setActiveTab('contact');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />

          <HakimProfileBanner
            onConsultClick={() => {
              setActiveTab('contact');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      )}

      {/* VIEW 2: SHOP / ALL MEDICINES & PRODUCTS */}
      {activeTab === 'shop' && (
        <main className="flex-1 py-6 sm:py-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          {/* Shop Page Banner */}
          <div className="bg-gradient-to-r from-pansar-900 to-pansar-950 text-white rounded-3xl p-5 sm:p-8 mb-6 shadow-md">
            <div className="max-w-2xl text-right">
              <span className="px-3 py-1 rounded-full bg-emerald-800 text-amber-300 text-xs font-bold font-urdu inline-block mb-2">
                آن لائن سٹور و ادویات خریدیے
              </span>
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-black font-title text-white mb-2">
                {lang === 'ur' ? 'خالص جڑی بوٹیاں اور قانون مفرد اعضاء کے مستند مرکبات' : 'All Natural Herbs & Tibb-e-Pakistani Formulations'}
              </h1>
              <p className="text-xs sm:text-sm text-emerald-100 font-urdu leading-relaxed">
                خالص پہاڑی جڑی بوٹیاں، خالص کشیدہ عرقیات، سفوف، معجون، اور شربت۔ اپنے پسندیدہ وزن کے مطابق آرڈر درج کروائیں۔
              </p>
            </div>
          </div>

          {/* Quick Category Horizontal Scroll Bar (Fast 1-touch filtering on mobile and desktop) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className={`shrink-0 px-3.5 py-2 rounded-2xl text-xs font-urdu font-bold transition-all flex items-center gap-2 shadow-2xs cursor-pointer group ${
                  selectedCategory === 'all'
                    ? 'bg-pansar-800 text-white shadow-emerald-900/20'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>{t('allCategories')}</span>
                <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                  selectedCategory === 'all' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  {(products || []).length}
                </span>
                {selectedCategory === 'all' && <Check className="w-3.5 h-3.5 text-amber-300" />}
              </button>

              {(categories || []).map(cat => {
                const isSelected = selectedCategory === cat.id;
                const catCount = (products || []).filter(p => p.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`shrink-0 px-3.5 py-2 rounded-2xl text-xs font-urdu font-bold transition-all flex items-center gap-2 shadow-2xs cursor-pointer group ${
                      isSelected
                        ? 'bg-pansar-800 text-white shadow-emerald-900/20'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span>{lang === 'ur' ? cat.nameUrdu : cat.nameEn}</span>
                    <span className={`text-[11px] font-sans font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }`}>
                      {catCount}
                    </span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-amber-300" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Desktop Left Filter Sidebar (4 cols) - HIDDEN ON MOBILE */}
            <div className="hidden lg:block lg:col-span-4 sticky top-24">
              <TibbiFilter
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedMizaj={selectedMizaj}
                setSelectedMizaj={setSelectedMizaj}
                selectedAilments={selectedAilments}
                toggleAilment={toggleAilment}
                inStockOnly={inStockOnly}
                setInStockOnly={setInStockOnly}
                onResetFilters={handleResetFilters}
              />
            </div>

            {/* Product Grid Area (8 cols on desktop, 12 cols / full width on mobile) */}
            <div className="lg:col-span-8 space-y-5 w-full">
              
              {/* Results Top bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl p-3.5 sm:p-4 shadow-sm border border-gray-100">
                <div className="text-xs sm:text-sm font-bold text-gray-700 font-urdu flex items-center gap-1.5">
                  <span>{t('showingResults')} </span>
                  <span className="text-pansar-800 font-sans font-black bg-pansar-50 px-2 py-0.5 rounded-lg border border-pansar-100">
                    {filteredProducts.length}
                  </span>
                  <span> ادویات</span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Mobile Filter Button (Visible only on mobile/tablet screens < lg) */}
                  <button
                    type="button"
                    onClick={() => setIsMobileFilterOpen(true)}
                    className="lg:hidden flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-urdu font-bold text-xs shadow-sm transition active:scale-95 cursor-pointer"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5 text-amber-300" />
                    <span>طبی فلٹرز و مزاج</span>
                    {activeFiltersCount > 0 && (
                      <span className="w-4 h-4 rounded-full bg-amber-400 text-pansar-950 font-sans text-[10px] font-black flex items-center justify-center mr-0.5">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>

                  {activeFiltersCount > 0 && (
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="text-xs text-rose-600 hover:text-rose-700 font-urdu font-bold px-2 py-1 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                    >
                      {t('resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Active Filter Chips / Removable Tags */}
              {activeFiltersCount > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 bg-emerald-50/60 p-2.5 rounded-2xl border border-emerald-100">
                  <span className="text-[11px] font-bold text-emerald-900 font-urdu ml-1">فلٹرز:</span>
                  
                  {selectedCategory !== 'all' && (
                    <span className="inline-flex items-center gap-1 bg-white text-emerald-800 px-2.5 py-1 rounded-xl text-xs font-urdu border border-emerald-200 shadow-2xs">
                      <span>کیٹیگری: {categories.find(c => c.id === selectedCategory)?.nameUrdu || selectedCategory}</span>
                      <button type="button" onClick={() => setSelectedCategory('all')} className="hover:text-rose-600 font-bold ml-1 cursor-pointer">×</button>
                    </span>
                  )}

                  {selectedMizaj !== 'all' && (
                    <span className="inline-flex items-center gap-1 bg-white text-amber-800 px-2.5 py-1 rounded-xl text-xs font-urdu border border-amber-200 shadow-2xs">
                      <span>مزاج: {mizajList?.find(m => m.id === selectedMizaj)?.nameUrdu || selectedMizaj}</span>
                      <button type="button" onClick={() => setSelectedMizaj('all')} className="hover:text-rose-600 font-bold ml-1 cursor-pointer">×</button>
                    </span>
                  )}

                  {selectedAilments.map(ailId => (
                    <span key={ailId} className="inline-flex items-center gap-1 bg-white text-rose-800 px-2.5 py-1 rounded-xl text-xs font-urdu border border-rose-200 shadow-2xs">
                      <span>علاج: {ailments?.find(a => a.id === ailId)?.nameUrdu || ailId}</span>
                      <button type="button" onClick={() => toggleAilment(ailId)} className="hover:text-rose-600 font-bold ml-1 cursor-pointer">×</button>
                    </span>
                  ))}

                  {inStockOnly && (
                    <span className="inline-flex items-center gap-1 bg-white text-pansar-800 px-2.5 py-1 rounded-xl text-xs font-urdu border border-pansar-200 shadow-2xs">
                      <span>صرف دستیاب اسٹاک</span>
                      <button type="button" onClick={() => setInStockOnly(false)} className="hover:text-rose-600 font-bold ml-1 cursor-pointer">×</button>
                    </span>
                  )}

                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 bg-white text-gray-800 px-2.5 py-1 rounded-xl text-xs font-urdu border border-gray-200 shadow-2xs">
                      <span>تلاش: "{searchQuery}"</span>
                      <button type="button" onClick={() => setSearchQuery('')} className="hover:text-rose-600 font-bold ml-1 cursor-pointer">×</button>
                    </span>
                  )}
                </div>
              )}

              {/* Product Cards */}
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm space-y-4">
                  <Package className="w-16 h-16 text-gray-300 mx-auto" />
                  <h3 className="text-lg font-bold text-gray-800 font-urdu">
                    {t('noProductsFound')}
                  </h3>
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="px-5 py-2.5 rounded-xl bg-pansar-800 text-white font-bold text-xs font-urdu cursor-pointer"
                  >
                    {t('resetFilters')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}

            </div>

          </div>
        </main>
      )}

      {/* VIEW 3: CATEGORIES OVERVIEW */}
      {activeTab === 'categories' && (
        <main className="flex-1 py-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h1 className="text-3xl sm:text-4xl font-black text-pansar-950 font-title mb-2">
              اقسام ادویات و پنسار
            </h1>
            <p className="text-sm text-gray-600 font-urdu">
              اپنی مطلوبہ کیٹیگری منتخب کر کے خالص نباتاتی ادویات براؤز کریں۔
            </p>
          </div>

          <div className="space-y-12">
            {(categories || []).map(cat => {
              const catProducts = products.filter(p => p.category === cat.id);
              return (
                <div key={cat.id} className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-black text-pansar-900 font-title">
                        {lang === 'ur' ? cat.nameUrdu : cat.nameEn}
                      </h2>
                      <p className="text-xs text-gray-500 font-urdu mt-0.5">
                        {lang === 'ur' ? cat.taglineUrdu : cat.taglineEn}
                      </p>
                    </div>

                    <button
                      onClick={() => handleSelectCategoryFromHome(cat.id)}
                      className="px-4 py-2 rounded-xl bg-pansar-50 text-pansar-900 hover:bg-pansar-100 text-xs font-bold font-urdu transition border border-pansar-200"
                    >
                      اس کیٹیگری کی تمام ادویات دیکھیں ({catProducts.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {catProducts.slice(0, 4).map(p => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      )}

      {/* VIEW 4: BLOGS & HEALTH ARTICLES */}
      {activeTab === 'blogs' && (
        <main className="flex-1">
          <BlogListPage
            onSelectArticle={(article) => {
              setSelectedProduct(null);
              setSelectedArticle(article);
              setActiveTab('article-detail');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onBackToHome={() => {
              setSelectedArticle(null);
              setActiveTab('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      )}

      {/* VIEW 5: TIBBI GUIDE & MIZAJ */}
      {activeTab === 'tibbi-guide' && (
        <main className="flex-1 py-10">
          <TibbiDiagnosisGuide products={products} />
          <HakimProfileBanner
            onConsultClick={() => {
              setActiveTab('contact');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      )}

      {/* VIEW 6: ABOUT US & STORE INTRODUCTION */}
      {(activeTab === 'about-hakim' || activeTab === 'about-us') && (
        <main className="flex-1">
          <AboutUsPage
            onShopClick={() => {
              setActiveTab('shop');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onContactClick={() => {
              setActiveTab('contact');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onTibbiClick={() => {
              setActiveTab('tibbi-guide');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </main>
      )}

      {/* VIEW 7: CONTACT & CLINIC LOCATION */}
      {activeTab === 'contact' && (
        <main className="flex-1">
          <ContactPage />
        </main>
      )}
        </>
      )}

      {/* Modals & Popups */}
      <CartDrawer />
      <CheckoutModal />
      {Boolean(selectedProductModal) && <ProductDetailModal />}
      <SocialProofToast isSuppressed={activeTab === 'admin'} />

      {/* Mobile Filter Drawer Modal */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 flex justify-end lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity cursor-pointer"
            onClick={() => setIsMobileFilterOpen(false)}
            aria-label="بند کریں"
          />

          {/* Drawer Container */}
          <div className="relative w-full max-w-sm sm:max-w-md bg-white shadow-2xl flex flex-col h-full z-10">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-pansar-900 text-white shadow-sm">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-amber-300" />
                <h3 className="font-bold text-base font-urdu text-white">
                  طبی فلٹرز و مزاج رہنمائی
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(false)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white text-gray-900 hover:bg-rose-600 hover:text-white transition-all shadow font-bold text-xs font-title cursor-pointer"
                title="بند کریں"
              >
                <X className="w-4 h-4" />
                <span>بند کریں</span>
              </button>
            </div>

            {/* Scrollable Filter Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              <TibbiFilter
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedMizaj={selectedMizaj}
                setSelectedMizaj={setSelectedMizaj}
                selectedAilments={selectedAilments}
                toggleAilment={toggleAilment}
                inStockOnly={inStockOnly}
                setInStockOnly={setInStockOnly}
                onResetFilters={handleResetFilters}
                isMobileModal={true}
              />
            </div>

            {/* Drawer Footer & Action */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center gap-2.5 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  setIsMobileFilterOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex-1 py-3 px-4 rounded-2xl bg-pansar-800 hover:bg-pansar-900 text-white font-urdu font-bold text-sm shadow-md transition text-center cursor-pointer"
              >
                <span>نتائج دیکھیں ({filteredProducts.length} ادویات)</span>
              </button>

              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="py-3 px-3.5 rounded-2xl bg-rose-50 hover:bg-rose-100 text-rose-700 font-urdu font-bold text-xs border border-rose-200 transition cursor-pointer"
                  title="تمام فلٹرز ختم کریں"
                >
                  ری سیٹ
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <AdminLoginModal
        isOpen={adminLoginOpen}
        onClose={() => setAdminLoginOpen(false)}
        onSuccess={() => {
          setAdminLoginOpen(false);
          setActiveTab('admin');
        }}
      />


      {/* Footer */}
      <Footer
        onSelectCategory={handleSelectCategoryFromHome}
        onOpenAdminLogin={() => {
          if (isAdminAuthenticated) {
            setActiveTab('admin');
          } else {
            setAdminLoginOpen(true);
          }
        }}
      />

    </div>
  );
}

export default App;
